import { useEffect, useRef, useState, type SetStateAction } from 'react';

const PURGE_KEY = 'petrhythm.purge_epoch';
const epoch = () => { try { return localStorage.getItem(PURGE_KEY) || '0'; } catch { return '0'; } };

/** Local input survives reload without another tab clearing it. A device purge invalidates every tab. */
export function useLocalDraft<T>(key: string, initial: T) {
  const storageKey = `petrhythm.draft.${key}`;
  const generation = useRef(epoch());
  const invalidated = useRef(false);
  const [value, setValue] = useState<T>(() => {
    try {
      const session = sessionStorage.getItem(storageKey); if (session === '__cleared__') return initial;
      const raw = session || localStorage.getItem(storageKey); if (!raw) return initial;
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object' && 'epoch' in parsed) return parsed.epoch === generation.current && !parsed.cleared ? { ...initial, ...parsed.value } : initial;
      return generation.current === '0' ? { ...initial, ...parsed } : initial;
    } catch { return initial; }
  });
  const current = useRef(value); const [storageError, setStorageError] = useState(false);
  useEffect(() => {
    const reset = () => { invalidated.current = true; generation.current = epoch(); current.current = initial; setValue(initial); try { sessionStorage.removeItem(storageKey); } catch { /* Global generation still rejects the old draft. */ } };
    const changed = (event: StorageEvent) => { if (event.key === PURGE_KEY) reset(); };
    window.addEventListener('storage', changed); window.addEventListener('petrhythm:device-purged', reset);
    return () => { window.removeEventListener('storage', changed); window.removeEventListener('petrhythm:device-purged', reset); };
  }, [storageKey]);
  const encode = (draft: T) => JSON.stringify({ epoch: generation.current, value: draft });
  const update = (next: SetStateAction<T>) => {
    if (invalidated.current || generation.current !== epoch()) return;
    const result = typeof next === 'function' ? (next as (value: T) => T)(current.current) : next;
    try { const raw = encode(result); sessionStorage.setItem(storageKey, raw); localStorage.setItem(storageKey, raw); setStorageError(false); } catch { setStorageError(true); }
    current.current = result; setValue(result);
  };
  const clear = (next = initial) => {
    try { if (localStorage.getItem(storageKey) === encode(current.current)) localStorage.removeItem(storageKey); sessionStorage.setItem(storageKey, JSON.stringify({ epoch: generation.current, cleared: true })); } catch { /* The committed record remains in Dexie. */ }
    current.current = next; setValue(next); setStorageError(false);
  };
  return { value, update, clear, storageError };
}
